import { useEffect } from 'react';
import { consumeEmailReturn, rememberEmailReturn } from '../../lib/email-authoring-draft';
import { useMutation, useQuery } from '@tanstack/react-query';
import { emailApi, emailKey } from '../../lib/email-api';
import { gmailReturnUrl, gmailConnectionError } from '../../lib/gmail-connection';
import { GmailMark } from './MailSurface';
import styles from './GmailPreferences.module.css';

export function useGmailConnection() {
  return useQuery({ queryKey: [...emailKey(), 'connections'], queryFn: emailApi.connections,
    retry: false, retryOnMount: false, staleTime: 15_000,
    refetchInterval: q => {
      if (q.state.status === 'error' || !q.state.data) return false;
      if (q.state.data.connections.some(c => ['disconnecting', 'importing'].includes(c.status) ||
          ['queued', 'running'].includes(c.sync?.status ?? ''))) return 5000;
      return q.state.data.connections.some(c => c.status === 'active') ? 60_000 : false;
    } });
}

export function ConnectGmailButton({ reconnect = false, disabled = false }: { reconnect?: boolean; disabled?: boolean }) {
  const connect = useMutation({ mutationFn: () => { rememberEmailReturn(); return emailApi.start(gmailReturnUrl()); },
    onSuccess: data => window.location.assign(data.authorizationUrl) });
  return <div>
    <button type="button" aria-label={connect.isPending ? 'Opening Google…' : reconnect ? 'Reconnect Gmail' : 'Connect Gmail'} className={styles.primary} disabled={disabled || connect.isPending} onClick={() => connect.mutate()}>
      <GmailMark />{connect.isPending ? 'Opening Google…' : reconnect ? 'Reconnect Gmail' : 'Connect Gmail'}
    </button>
    {(connect.error || gmailConnectionError(window.location.search)) &&
      <p role="alert">{connect.error?.message ?? gmailConnectionError(window.location.search)}</p>}
  </div>;
}

export function GmailConnectionNotice({ showConnected = true }: { showConnected?: boolean }) {
  useEffect(() => { const path = consumeEmailReturn(); if (path) window.location.replace(`${path}${window.location.search}`); }, []);
  const query = useGmailConnection();
  const connection = query.data?.connections[0];
  if (query.isLoading) return <p role="status">Checking Gmail…</p>;
  if (query.isError) return <p role="alert">Couldn’t check Gmail. <button onClick={() => void query.refetch()}>Try again</button></p>;
  if (connection && ['active', 'importing'].includes(connection.status)) return showConnected ? <div className={styles.accountLine}><GmailMark /><span>{connection.accountEmail}</span><a href="/app/settings/gmail">Manage</a></div> : null;
  return <div className={styles.connectionNotice}>
    <p>{connection?.status === 'disconnecting' ? 'Disconnecting Gmail…' : connection?.status === 'paused' ? 'Gmail is paused. Resume in settings to activate a sequence.' : 'Connect Gmail when you’re ready to use your sequences.'}</p>
    {connection && ['paused', 'disconnecting'].includes(connection.status) ? <a href="/app/settings/gmail">Gmail settings</a> :
      <ConnectGmailButton reconnect={connection?.status === 'reconnect_required'} disabled={!(query.data?.canConnect ?? query.data?.enabled)} />}
    {query.data?.setupIssue && <p role="status">{query.data.setupIssue.message}</p>}
  </div>;
}
